import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Banknote, Loader2, ArrowRight, CheckCircle2, AlertTriangle, X } from "lucide-react";

export const PayslipCheckWidget = () => {
    const [data, setData] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const year = new Date().getFullYear();
        Promise.all([
            api.get("/documents/payslips/report", { params: { year } }),
            api.get("/documents/payslips/reminders", { params: { year } }),
        ])
            .then(([r1, r2]) => {
                setData(r1.data);
                setReminders(r2.data.items || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const dismissReminder = async (item) => {
        try {
            await api.post("/documents/payslips/reminders/dismiss", {
                year: data.year,
                period_key: item.period_key,
                label: item.label,
            });
            setReminders((prev) => prev.filter((x) => x.period_key !== item.period_key));
            toast.success(`Promemoria "${item.label}" eliminato`);
        } catch (e) {
            toast.error("Errore nell'eliminazione del promemoria");
        }
    };

    if (loading) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="payslip-check-widget">
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                </div>
            </section>
        );
    }
    if (!data) return null;
    const t = data.totals;
    const ok = t.with_missing === 0;

    return (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="payslip-check-widget">
            <h2 className="mb-1 flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                <Banknote className="h-4 w-4 text-[#376DB3]" />
                Controllo buste paga {data.year}
            </h2>
            {ok ? (
                <p className="mb-3 flex items-center gap-1.5 text-xs text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Tutte le buste paga dovute sono in archivio ({t.checked} dipendenti)
                </p>
            ) : (
                <p className="mb-3 text-xs text-slate-500">
                    <b className="text-red-600">{t.with_missing}</b> dipendenti su {t.checked} con buste mancanti
                </p>
            )}

            {reminders.length > 0 && (
                <div className="mb-3 space-y-2" data-testid="payslip-reminders">
                    {reminders.map((item) => (
                        <div
                            key={item.period_key}
                            className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5"
                            data-testid={`payslip-reminder-${item.period_key}`}
                        >
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold text-amber-800">
                                    {item.label} incompleto
                                    <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-px font-mono-numbers text-[10px] font-bold text-amber-700">
                                        {item.missing} {item.missing === 1 ? "busta mancante" : "buste mancanti"}
                                    </span>
                                </div>
                                <div className="mt-0.5 truncate text-[11px] text-amber-700/80">
                                    {item.employees.join(", ")}{item.more > 0 ? ` e altri ${item.more}` : ""}
                                </div>
                            </div>
                            <button
                                onClick={() => dismissReminder(item)}
                                className="shrink-0 rounded-md p-1 text-amber-400 transition-colors hover:bg-amber-100 hover:text-amber-700"
                                title="Elimina promemoria (busta paga non emessa)"
                                data-testid={`payslip-reminder-dismiss-${item.period_key}`}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Link
                to="/documenti/buste-paga"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#376DB3] hover:underline"
                data-testid="payslip-widget-link"
            >
                Vai al controllo <ArrowRight className="h-3 w-3" />
            </Link>
        </section>
    );
};
